import { useId } from 'react'
import type { JSX } from 'react'

import { Rectangle } from 'recharts'

// Diagonal-line hatch for a comparison bar. Denser than NoDataHatch's faint
// wash so the stripes stay legible on a narrow column.
const HATCH_TILE_SIZE = 7
const HATCH_STROKE_WIDTH = 1.5
// Corner segments overhang the tile so the 45° lines join seamlessly across tiles.
const HATCH_CORNER_OVERLAP = 1.5
// Faint series-colour fill behind the stripes so short/thin bars still read as
// the series colour.
const HATCH_BACKGROUND_OPACITY = 0.14

interface ComparisonHatchBarProps {
  /** Series colour (the resolved teal); recharts injects this from the Bar's
   *  `fill`. Drives both the faint background and the stripes. */
  fill?: string
  /** Corner radii; omit (undefined) for flat corners, e.g. a stacked segment. */
  radius?: [number, number, number, number]
  // Column geometry injected by recharts per data point.
  x?: number
  y?: number
  width?: number
  height?: number
}

/**
 * Custom recharts `Bar` `shape` for a comparison series: a faint series-colour
 * fill overlaid with solid diagonal stripes, so a comparison column is
 * distinguishable by pattern — not colour alone — mirroring the dashed
 * comparison line. Recharts injects each column's `x`/`y`/`width`/`height` and
 * the Bar's `fill`; this renders a self-contained SVG `<pattern>` (no
 * chart-level `<defs>` host needed) and paints two rounded rects with it.
 */
export function ComparisonHatchBar({
  fill,
  radius,
  x,
  y,
  width,
  height,
}: ComparisonHatchBarProps): JSX.Element | null {
  // `useId` output contains colons; strip them for a valid `url(#…)` reference.
  const patternId = `comparison-hatch-${useId().replace(/:/g, '')}`

  if (
    fill == null ||
    x == null ||
    y == null ||
    width == null ||
    height == null ||
    width <= 0
  ) {
    return null
  }

  const tile = HATCH_TILE_SIZE
  const overlap = HATCH_CORNER_OVERLAP
  // Main diagonal plus two corner segments so adjacent tiles join seamlessly.
  const linePath =
    `M0,${tile} L${tile},0 ` +
    `M-${overlap},${overlap} L${overlap},-${overlap} ` +
    `M${tile - overlap},${tile + overlap} L${tile + overlap},${tile - overlap}`

  return (
    <g>
      <defs>
        <pattern
          id={patternId}
          patternUnits="userSpaceOnUse"
          width={tile}
          height={tile}
        >
          <path d={linePath} stroke={fill} strokeWidth={HATCH_STROKE_WIDTH} />
        </pattern>
      </defs>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        radius={radius}
        fill={fill}
        fillOpacity={HATCH_BACKGROUND_OPACITY}
      />
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        radius={radius}
        fill={`url(#${patternId})`}
      />
    </g>
  )
}
